import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  Divider,
  IconButton,
  Radio,
  RadioGroup,
  Skeleton,
  Typography,
} from "@mui/material";

import { GinoAddOns } from "../../../api/services/ginoAddOns";
import { useGetGinoAddOns } from "../../../api/queries/ginoAddOns";
import { useCheckoutGinoAddon } from "../../../api/mutations/ginoCheckout";
import { CloseButtonIcon, QuickBoostPackIcon } from "../../../assets/icons/gino-icons";

export const AddOnsStep = ({
  onClose,
  selectedPack,
  setSelectedPack,
  handleOpenStripePayModal,
}: {
  onClose: () => void;
  selectedPack: GinoAddOns | null;
  setSelectedPack: (pack: GinoAddOns | null) => void;
  handleOpenStripePayModal: (clientSecret: string, publishableKey: string) => void;
}) => {
  const { t } = useTranslation("gino", { keyPrefix: "component.ginoCreditModal" });

  const { data: packs, isLoading: isLoadingPacks, isError: isErrorPacks } = useGetGinoAddOns();
  const handleSelectPack = (pack: GinoAddOns) => setSelectedPack(pack);

  const { mutateAsync: checkoutAddon, isPending: isLoadingCheckout } = useCheckoutGinoAddon({});

  const handlePurchase = async () => {
    if (!selectedPack) return;
    try {
      const { clientSecret, publishableKey } = await checkoutAddon({ addonId: selectedPack.id });
      handleOpenStripePayModal(clientSecret, publishableKey);
    } catch (error) {
      console.error("Checkout failed:", error);
    }
  };

  return (
    <Box sx={styles.modalContent}>
      <Box sx={styles.header}>
        <Typography sx={styles.title}>{t("addOnsTitle")}</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseButtonIcon />
        </IconButton>
      </Box>
      <Divider flexItem orientation="horizontal" />
      <Box sx={styles.addOnsContainer}>
        <Typography sx={styles.title}>{t("selectAddOns")}</Typography>
        <Typography sx={styles.description}>{t("selectAddOnsDescription")}</Typography>

        {isLoadingPacks && Array.from({ length: 2 }).map((_, index) => <AddOnLoadingPack key={index} />)}
        {!isLoadingPacks && packs && (
          <RadioGroup sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            {packs.map((pack) => (
              <AddOnPack key={pack.id} pack={pack} currentPack={selectedPack} onSelectPack={handleSelectPack} />
            ))}
          </RadioGroup>
        )}
      </Box>
      <Divider flexItem orientation="horizontal" />
      <Box sx={styles.buttonContainer}>
        <Button variant="outlined-md" onClick={onClose}>
          {t("cancel")}
        </Button>
        <Button variant="primary-md" onClick={handlePurchase} disabled={!selectedPack || isLoadingCheckout}>
          {t("purchaseNow")}
        </Button>
      </Box>
    </Box>
  );
};

const AddOnPack = ({
  pack,
  currentPack,
  onSelectPack,
}: {
  pack: GinoAddOns;
  currentPack: GinoAddOns | null;
  onSelectPack: (pack: GinoAddOns) => void;
}) => {
  const packPrice = `${(pack.priceCents / 100).toFixed(2)} ${pack.currency}`;
  const isSelected = currentPack?.id === pack.id;

  return (
    <Card key={pack.id} sx={styles.addOnPack(isSelected)} onClick={() => onSelectPack(pack)}>
      <CardActionArea sx={styles.addOnPackContent} disableRipple>
        <Box sx={styles.addOnPackHeader}>
          <Typography sx={styles.addOnPackTitle}>
            <Box sx={styles.addOnPackIcon}>
              <QuickBoostPackIcon />
            </Box>
            {pack.title}
          </Typography>
          <Radio value={pack.id} checked={isSelected} />
        </Box>
        <Divider flexItem orientation="horizontal" sx={styles.addOnPackDivider(isSelected)} />
        <Box sx={styles.addOnPackPriceContainer}>
          <Typography sx={styles.addOnPackPrice}>{packPrice}</Typography>
          <Typography sx={styles.addOnPackDescription}>{pack.description}</Typography>
        </Box>
      </CardActionArea>
    </Card>
  );
};

const AddOnLoadingPack = () => {
  return (
    <Card sx={{ ...styles.addOnPack(false), "& .MuiCardActionArea-root": { cursor: "default" } }}>
      <CardActionArea sx={styles.addOnPackContent} disableRipple>
        <Box sx={styles.addOnPackHeader}>
          <Typography sx={styles.addOnPackTitle}>
            <Box sx={styles.addOnPackIcon}>
              <QuickBoostPackIcon />
            </Box>
            <Skeleton variant="text" width={100} height={24} />
          </Typography>
          <Skeleton variant="circular" width={30} height={30} />
        </Box>
        <Divider flexItem orientation="horizontal" />
        <Box sx={styles.addOnPackPriceContainer}>
          <Skeleton variant="text" width={60} height={24} />
          <Skeleton variant="text" width={350} height={20} />
          <Skeleton variant="text" width={250} height={20} />
        </Box>
      </CardActionArea>
    </Card>
  );
};

const styles = {
  modalContent: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    width: "640px",
    borderRadius: "var(--radius-xl, 12px)",
    background: "var(--Colors-Background-bg-primary, #FFF)",
    boxShadow:
      "0 20px 24px -4px var(--Colors-Effects-Shadows-shadow-xl_01, rgba(16, 24, 40, 0.08)), 0 8px 8px -4px var(--Colors-Effects-Shadows-shadow-xl_02, rgba(16, 24, 40, 0.03))",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 24px",
  },
  title: {
    color: "var(--colors-text-text-primary-900, #101828)",
    fontFamily: "Montserrat",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  description: {
    color: "var(--Gray-600, #475467)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  addOnsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "12px 24px",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: "12px 24px",
    gap: "12px",
  },
  addOnPack: (isSelected: boolean) => ({
    flex: 1,
    borderRadius: "12px",
    border: isSelected ? "2px solid var(--Brand-600, #008B07)" : "1px solid var(--Gray-200, #E4E7EC)",
    background: "var(--Base-White, #FFF)",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    position: "relative",
  }),
  addOnPackContent: {
    display: "flex",
    flexDirection: " column",
    gap: "16px",
    alignItems: "flex-start",
    "& .MuiCardActionArea-focusHighlight": {
      backgroundColor: "transparent !important",
    },
  },
  addOnPackTitle: {
    color: "var(--Gray-700, #344054)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: "8px",
  },
  addOnPackIcon: {
    borderRadius: "6px",
    border: "1px solid var(--Gray-200, #E4E7EC)",
    background: "var(--Base-White, #FFF)",
    boxShadow: "0 1px 2px 0 rgba(16, 24, 40, 0.05)",
    padding: "8px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  addOnPackHeader: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 12px 0",
  },
  addOnPackDescription: {
    fontFamily: "Montserrat",
    color: "var(--Gray-600, #475467)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  addOnPackPrice: {
    fontFamily: "Montserrat",
    color: "var(--Gray-700, #344054)",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "30px",
  },
  addOnPackPriceContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    padding: "0 12px 12px",
  },
  addOnPackDivider: (isSelected: boolean) => ({
    borderColor: isSelected ? "var(--Brand-600, #008B07)" : "var(--Gray-200, #E4E7EC)",
    borderWidth: isSelected ? "2px" : "1px",
  }),
};
